import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useT } from "@/i18n/LanguageContext";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ScammerWatchlist from "@/components/ScammerWatchlist";
import SectionHeading from "@/components/SectionHeading";
import api from "@/lib/api";
import { asArray, asStringArray, formatCr } from "@/lib/safeData";
import { Search, Users, AlertTriangle, ChevronRight } from "lucide-react";

const statusClass = (s) => ({
  high_risk: "tag-high-risk",
  reported_cybercrime: "tag-cybercrime",
  sebi_warning: "tag-sebi-warning",
  multiple_complaints: "tag-multiple",
  under_investigation: "tag-investigating",
}[s] || "tag-investigating");

const Database = () => {
  const { t } = useT();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status !== "all") params.status = status;
      const res = await api.get("/scammers", { params });
      setItems(asArray(res.data));
    } finally {
      setLoading(false);
    }
  }, [q, status]);

  const isFirstLoad = useRef(true);
  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      void load();
      return;
    }
    const id = setTimeout(() => void load(), 250);
    return () => clearTimeout(id);
  }, [load]);

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-8 py-16 md:py-24" data-testid="database-page">
      <SectionHeading eyebrow="Verified Database" title={t.database.title} subtitle={t.database.subtitle} />

      <ScammerWatchlist limit={6} />

      <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-8 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
          <Input
            placeholder={t.database.searchPlaceholder}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-10 h-12 bg-white/[0.03] border-white/15 focus-visible:border-[#D4AF37] focus-visible:ring-[#D4AF37]/30 text-white placeholder:text-white/30"
            data-testid="db-search-input"
          />
        </div>
        <div className="md:col-span-4">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-12 bg-white/[0.03] border-white/15 text-white" data-testid="db-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10 text-white">
              <SelectItem value="all">{t.database.filterAll}</SelectItem>
              <SelectItem value="high_risk">{t.database.statusLabels.high_risk}</SelectItem>
              <SelectItem value="reported_cybercrime">{t.database.statusLabels.reported_cybercrime}</SelectItem>
              <SelectItem value="sebi_warning">{t.database.statusLabels.sebi_warning}</SelectItem>
              <SelectItem value="multiple_complaints">{t.database.statusLabels.multiple_complaints}</SelectItem>
              <SelectItem value="under_investigation">{t.database.statusLabels.under_investigation}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-8 space-y-4" data-testid="scammer-list">
        {loading && <div className="text-center py-12 text-white/40">{t.common.loading}</div>}
        {!loading && asArray(items).length === 0 && (
          <div className="surface-card p-10 text-center text-white/55">{t.database.noResults}</div>
        )}
        {asArray(items).map((s, i) => (
          <Link key={s.id ?? `scammer-${i}`} to={`/database/${s.id ?? ""}`} className="block surface-card p-6 group" data-testid={`scammer-row-${s.id ?? i}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-display text-xl font-bold truncate">{s.handle}</h3>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-mono-alt ${statusClass(s.status_tag)}`}>
                    <AlertTriangle className="h-3 w-3" />
                    {t.database.statusLabels[s.status_tag] || s.status_tag}
                  </span>
                </div>
                <div className="mt-1 text-xs text-white/50 font-mono-alt">
                  {asStringArray(s.aliases).slice(0, 3).join(" · ")}
                </div>
                <p className="mt-3 text-sm text-white/70 line-clamp-2 leading-relaxed">{s.summary}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {asStringArray(s.scam_methods).slice(0, 4).map((m) => (
                    <span key={m} className="text-[10px] uppercase tracking-wider text-white/60 bg-white/5 border border-white/10 px-2 py-1 rounded">
                      {m}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex flex-col items-end text-right">
                <div className="inline-flex items-center gap-1.5 text-white/60 text-xs"><Users className="h-3.5 w-3.5" /> {s.victim_count} {t.database.victims}</div>
                <div className="mt-2 font-mono-alt text-[#FF6A60] font-bold">{formatCr(s.total_loss_inr)}</div>
                <div className="mt-3 inline-flex items-center text-[#D4AF37] text-xs font-mono-alt group-hover:translate-x-1 transition-transform">
                  View profile <ChevronRight className="h-4 w-4" />
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default Database;
